import { formatBytes } from "./gpu";

/**
 * The floating loading indicator.
 *
 * Free's note: "When you click the lattice or the model the screen goes faded
 * while it loads but perhaps a big floating visual indicator of its loading
 * status and what is being loaded would be great."
 *
 * The waits here are genuinely long, hundreds of megabytes followed by twenty
 * or thirty seconds of shader compilation, so a dimmed screen with no
 * explanation reads as a hang. This names the artifact, shows real bytes, and
 * distinguishes the two phases, because "214 MB of 470 MB" and "compiling for
 * your GPU" are very different kinds of waiting and only one of them has a
 * progress bar that means anything.
 */

export type LoadPhase = "fetch" | "compile" | "decode" | null;

export type LoadingState = {
  phase: LoadPhase;
  /** "Ternary Bonsai 1.7B" or "The Mind's Gardener". */
  title: string;
  /** The file currently in flight, if any. */
  file?: string;
  loaded?: number;
  total?: number;
  pct: number;
};

export function LoadingOverlay({ state }: { state: LoadingState }) {
  if (!state.phase) return null;

  const known = state.total !== undefined && state.total > 0;
  const line =
    state.phase === "fetch" && known
      ? `${formatBytes(state.loaded ?? 0)} of ${formatBytes(state.total)}`
      : state.phase === "compile"
        ? "building shaders for your graphics card"
        : state.phase === "decode"
          ? "unpacking and checking the lattice"
          : "starting";

  return (
    <div className="loading-veil" role="status" aria-live="polite">
      <div className="loading-card">
        <div className="loading-what">{state.phase === "fetch" ? "Downloading" : "Preparing"}</div>
        <div className="loading-title">{state.title}</div>

        {/* Indeterminate while compiling: there is no honest percentage for
            shader compilation, and a fake one is worse than a moving stripe. */}
        <div className={`bar big ${state.phase === "fetch" && known ? "" : "indet"}`}>
          <i style={state.phase === "fetch" && known ? { width: `${state.pct.toFixed(1)}%` } : undefined} />
        </div>

        <div className="loading-line">
          {state.phase === "fetch" && known && <span className="loading-pct">{state.pct.toFixed(0)}%</span>}
          {line}
        </div>

        {state.file && <div className="loading-file">{state.file}</div>}

        <div className="loading-note">
          {state.phase === "fetch"
            ? "This downloads once and is cached, so later visits skip it."
            : "This happens once per session and cannot be sped up."}
        </div>
      </div>
    </div>
  );
}
